/**
 * QueueTable - Tabela de conversas na fila de atendimento
 * 
 * Exibe lista de conversas com informações do contato, inbox,
 * departamento, situação, tempo de espera e ações
 */

import { Eye, UserPlus } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { QueueItem, QueueSituation } from '@/hooks/useServiceQueue';
import { getStorageUrl } from '@/lib/storage';

interface QueueTableProps {
  items: QueueItem[];
  loading?: boolean;
  onAssign: (item: QueueItem) => void;
  onView: (item: QueueItem) => void; // Callback para abrir modal de conversa
}

// Configuração de badges por situação
const situationConfig: Record<QueueSituation, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  waiting_bot: { label: 'No Bot', variant: 'secondary' },
  waiting_agent: { label: 'Aguardando', variant: 'default' },
  pending_transfer: { label: 'Transferência', variant: 'destructive' },
  in_service: { label: 'Em Atendimento', variant: 'outline' },
  closed: { label: 'Encerrado', variant: 'secondary' },
};

// Formata tempo de espera
function formatWaitingTime(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours < 24) return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
}

// Trunca texto com limite de caracteres
function truncateText(text: string | null, maxLength: number): string {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
}

export function QueueTable({ items, loading, onAssign, onView }: QueueTableProps) {
  // Extrai iniciais do nome
  const getInitials = (name: string | null, phone: string | null): string => {
    if (name) {
      return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
    }
    if (phone) {
      return phone.substring(phone.length - 2);
    }
    return '??';
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="flex items-center gap-4 p-4 border border-border rounded-lg">
            <Skeleton className="h-10 w-10 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-48" />
            </div>
            <Skeleton className="h-6 w-20" />
            <Skeleton className="h-8 w-8" />
          </div>
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
          <UserPlus className="h-8 w-8 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-medium text-foreground mb-1">
          Fila vazia
        </h3>
        <p className="text-sm text-muted-foreground max-w-sm">
          Não há conversas aguardando atendimento no momento
        </p>
      </div>
    );
  }

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/50">
            <TableHead className="w-[280px]">Contato</TableHead>
            <TableHead className="w-[120px]">Inbox</TableHead>
            <TableHead className="w-[120px]">Departamento</TableHead>
            <TableHead className="w-[120px]">Situação</TableHead>
            <TableHead className="w-[100px]">Espera</TableHead>
            <TableHead className="w-[200px]">Última Mensagem</TableHead>
            <TableHead className="w-[100px] text-right">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item) => {
            const config = situationConfig[item.situation];
            const avatarUrl = item.contact?.avatar_url 
              ? getStorageUrl(item.contact.avatar_url) 
              : null;

            return (
              <TableRow 
                key={item.id}
                className="hover:bg-muted/30 transition-colors"
              >
                {/* Contato */}
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={avatarUrl || undefined} />
                        <AvatarFallback className="bg-primary/10 text-primary text-sm font-medium">
                          {getInitials(item.contact?.name, item.contact?.phone)}
                        </AvatarFallback>
                      </Avatar>
                      {item.unread_count > 0 && (
                        <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-destructive-foreground">
                          {item.unread_count > 9 ? '9+' : item.unread_count}
                        </span>
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-foreground truncate">
                        {truncateText(item.contact?.name || 'Sem nome', 22)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {item.contact?.phone || 'Sem telefone'}
                      </p>
                    </div>
                  </div>
                </TableCell>

                {/* Inbox */}
                <TableCell>
                  <Badge variant="outline" className="font-normal max-w-[100px] truncate">
                    {truncateText(item.inbox.name, 12)}
                  </Badge>
                </TableCell>

                {/* Departamento */}
                <TableCell>
                  {item.department ? (
                    <span className="text-sm text-foreground">
                      {truncateText(item.department.name, 15)}
                    </span>
                  ) : (
                    <span className="text-sm text-muted-foreground italic">
                      Sem dept.
                    </span>
                  )}
                </TableCell>

                {/* Situação */}
                <TableCell>
                  <Badge variant={config.variant} className="whitespace-nowrap">
                    {config.label}
                  </Badge>
                  {item.pending_transfer && (
                    <p className="text-[10px] text-muted-foreground mt-1">
                      → {item.pending_transfer.to_agent_name || item.pending_transfer.to_department_name || 'Agente'}
                    </p>
                  )}
                </TableCell>

                {/* Tempo de Espera */}
                <TableCell>
                  <span className={`text-sm font-medium ${
                    item.waiting_time > 3600 ? 'text-destructive' : 
                    item.waiting_time > 1800 ? 'text-amber-600' : 
                    'text-muted-foreground'
                  }`}>
                    {formatWaitingTime(item.waiting_time)}
                  </span>
                </TableCell>

                {/* Última Mensagem */}
                <TableCell>
                  <p className="text-sm text-muted-foreground truncate max-w-[180px]">
                    {truncateText(item.last_message_preview, 30) || 'Sem mensagens'}
                  </p>
                </TableCell>

                {/* Ações */}
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      title="Ver conversa"
                      onClick={() => onView(item)}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    {item.situation !== 'in_service' && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-primary hover:text-primary"
                        title="Atribuir agente"
                        onClick={() => onAssign(item)}
                      >
                        <UserPlus className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
